import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { formatPercentage } from '../../../../lib/utils/deepDiveUtils';

const QuadrantDetailTable = ({ quadrantName, quadrantColor, data, metricType = 'aware-interest' }) => {
  const [expanded, setExpanded] = useState(true);

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 2, border: `2px solid ${quadrantColor}20` }}>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          No data in this quadrant
        </Typography>
      </Paper>
    );
  }

  // Calculate total metric values for %Share
  // Each individual segment (dot on scatter) shown separately
  const totalAwareness = data.reduce((sum, record) => sum + (record.x || 0), 0);
  const segmentCount = data.length;

  // Sort segments by primary metric (x-axis value) descending
  const sortedSegments = [...data].sort((a, b) => (b.x || 0) - (a.x || 0));

  return (
    <Paper sx={{ border: `2px solid ${quadrantColor}`, borderRadius: 2 }}>
      {/* Header */}
      <Box
        sx={{
          p: 1.5,
          backgroundColor: quadrantColor + '10',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {quadrantName}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {segmentCount} segment{segmentCount !== 1 ? 's' : ''}
          </Typography>
          <IconButton size="small">
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      {/* Table */}
      <Collapse in={expanded}>
        <TableContainer sx={{ maxHeight: 300 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  Demographic Segment
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  % Share
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  X-Axis
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  Y-Axis
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSegments.map((segment, index) => {
                // Calculate share based on primary metric (x-axis value)
                const sharePercent = totalAwareness > 0 
                  ? Math.round((segment.x / totalAwareness) * 100) 
                  : 0;
                
                const segmentLabel = `${segment.ethnicity} / ${segment.agegroup} / ${segment.gender}`;
                
                return (
                  <TableRow
                    key={`${segment.ethnicity}-${segment.agegroup}-${segment.gender}-${index}`}
                    hover
                    sx={{
                      '&:hover': {
                        backgroundColor: quadrantColor + '05'
                      }
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.75rem' }}>
                      {segmentLabel}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      {sharePercent}%
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                      {formatPercentage(segment.x)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                      {formatPercentage(segment.y)}
                    </TableCell>
                  </TableRow>
                );
              })}
              
              {/* Total Row */}
              <TableRow sx={{ backgroundColor: quadrantColor + '15' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                  TOTAL ({segmentCount} segments)
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                  100%
                </TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
    </Paper>
  );
};

export default QuadrantDetailTable;

